import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Dashboard from "./Dashboard";
import Products from "./Products";
import Orders from "./Orders";
import Users from "./Users";
import Coupons from "./Coupons";
import Contacts from "./Contacts";
import Categories from "./Categories";
import Staff from "./Staff"; // ← NEW
import "../../styles/admin.css";

import {
    FiTag, FiPackage, FiUsers, FiGift, FiMail,
    FiLogOut, FiChevronLeft, FiChevronRight,
    FiBarChart2, FiAlertCircle, FiCheck, FiClock,
    FiX, FiMenu, FiUserPlus
} from "react-icons/fi";
import {
    FaStore, FaCrown, FaTruck, FaCheckCircle, FaUserShield, FaUserTie, FaUser
} from "react-icons/fa";
import { MdDashboard, MdCategory } from "react-icons/md";

// ── Role config ────────────────────────────────────────────
const ROLE_HIERARCHY = { user: 0, employee: 1, admin: 2, superadmin: 3 };

const ROLE_META = {
    superadmin: { label: "Super Admin", icon: <FaUserShield />, color: "#f59e0b" },
    admin: { label: "Administrator", icon: <FaCrown />, color: "#6366f1" },
    employee: { label: "Employee", icon: <FaUserTie />, color: "#10b981" },
    user: { label: "User", icon: <FaUser />, color: "#64748b" },
};

export const C = {
    bg: "#f5f6fa", surface: "#ffffff", card: "#ffffff",
    border: "rgba(99, 102, 241, 0.1)", primary: "#6366f1",
    primaryDark: "#4f46e5", accent: "#06b6d4", gold: "#f59e0b",
    green: "#10b981", red: "#ef4444", orange: "#f97316",
    blue: "#3b82f6", text: "#0f172a", muted: "#64748b", white: "#ffffff"
};

export const STATUS_COLORS = {
    Processing: { bg: "rgba(245,158,11,0.1)", text: "#d97706", border: "rgba(245,158,11,0.25)" },
    Confirmed: { bg: "rgba(99,102,241,0.1)", text: "#6366f1", border: "rgba(99,102,241,0.25)" },
    Shipped: { bg: "rgba(6,182,212,0.1)", text: "#0891b2", border: "rgba(6,182,212,0.25)" },
    "Out for Delivery": { bg: "rgba(249,115,22,0.1)", text: "#ea580c", border: "rgba(249,115,22,0.25)" },
    Delivered: { bg: "rgba(16,185,129,0.1)", text: "#059669", border: "rgba(16,185,129,0.25)" },
    Cancelled: { bg: "rgba(239,68,68,0.1)", text: "#dc2626", border: "rgba(239,68,68,0.25)" },
};

// ── Toast ──────────────────────────────────────────────────
export function Toast({ toasts }) {
    return (
        <div className="toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`toast toast-${t.type === "error" ? "error" : "success"}`}>
                    <span className="toast-icon">
                        {t.type === "error" ? <FiAlertCircle /> : <FiCheck />}
                    </span>
                    <span>{t.msg}</span>
                </div>
            ))}
        </div>
    );
}

// ── Stat Card ──────────────────────────────────────────────
export function Stat({ icon, label, value, sub, color = C.primary }) {
    return (
        <div className="stat-card">
            <div className="stat-card-icon" style={{ background: color + "18", color }}>{icon}</div>
            <div className="stat-card-body">
                <div className="stat-card-label">{label}</div>
                <div className="stat-card-value">{value}</div>
                {sub && <div className="stat-card-sub">{sub}</div>}
            </div>
            <div className="stat-card-accent" style={{ background: color }} />
        </div>
    );
}

// ── Badge ──────────────────────────────────────────────────
export function Badge({ status }) {
    const badgeClass = status ? status.toLowerCase().replace(/\s+/g, '-') : 'default';
    return (
        <span className={`badge badge-${badgeClass}`}>
            {status === "Processing" && <FiClock />}
            {status === "Confirmed" && <FiCheck />}
            {(status === "Shipped" || status === "Out for Delivery") && <FaTruck />}
            {status === "Delivered" && <FaCheckCircle />}
            {status === "Cancelled" && <FiX />}
            {status}
        </span>
    );
}

// ── Input ──────────────────────────────────────────────────
export function Input({ label, className = "", ...props }) {
    return (
        <div className="input-group">
            {label && <label className="input-label">{label}</label>}
            <input {...props} className={`form-control ${className}`} />
        </div>
    );
}

// ── Select ─────────────────────────────────────────────────
export function Select({ label, children, className = "", ...props }) {
    return (
        <div className="input-group">
            {label && <label className="input-label">{label}</label>}
            <select {...props} className={`form-control ${className}`}>{children}</select>
        </div>
    );
}

// ── Button ─────────────────────────────────────────────────
export function Btn({ children, variant = "primary", size = "md", className = "", ...props }) {
    return (
        <button {...props} className={`btn btn-${variant} btn-${size} ${className}`}>
            {children}
        </button>
    );
}

// ── Modal ──────────────────────────────────────────────────
export function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="modal-content">
                <div className="modal-header">
                    <h3 className="modal-title">{title}</h3>
                    <button onClick={onClose} className="modal-close"><FiX /></button>
                </div>
                <div className="modal-body">{children}</div>
            </div>
        </div>
    );
}

// ── Table ──────────────────────────────────────────────────
export function Table({ columns, data, loading, emptyMsg = "No data found" }) {
    return (
        <div className="table-wrapper">
            <table className="data-table">
                <thead>
                    <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                    {loading ? (
                        [...Array(5)].map((_, i) => (
                            <tr key={i}>
                                {columns.map(c => (
                                    <td key={c.key}><div className="skeleton-row" /></td>
                                ))}
                            </tr>
                        ))
                    ) : data.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="empty-table-msg">{emptyMsg}</td>
                        </tr>
                    ) : (
                        data.map((row, i) => (
                            <tr key={i}>
                                {columns.map(c => (
                                    <td key={c.key}>
                                        {c.render ? c.render(row[c.key], row) : row[c.key] ?? "—"}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

// ── Pagination ─────────────────────────────────────────────
export function Pagination({ page, pages, onChange }) {
    if (pages <= 1) return null;
    return (
        <div className="pagination-wrapper">
            <Btn variant="ghost" size="sm" disabled={page === 1} onClick={() => onChange(page - 1)}>
                <FiChevronLeft /> Prev
            </Btn>
            {[...Array(Math.min(pages, 7))].map((_, i) => {
                const p = i + 1;
                return (
                    <button
                        key={p}
                        onClick={() => onChange(p)}
                        className={`page-btn ${p === page ? 'active' : 'inactive'}`}
                    >
                        {p}
                    </button>
                );
            })}
            <Btn variant="ghost" size="sm" disabled={page === pages} onClick={() => onChange(page + 1)}>
                Next <FiChevronRight />
            </Btn>
        </div>
    );
}

// ── Bar Chart ──────────────────────────────────────────────
export function BarChart({ data, labelKey, valueKey, color = C.primary }) {
    if (!data?.length) return <div className="barchart-empty"><FiBarChart2 /> No data yet</div>;
    const max = Math.max(...data.map(d => d[valueKey]));
    return (
        <div className="barchart-wrapper">
            {data.map((d, i) => (
                <div key={i} className="barchart-col">
                    <div className="barchart-val">
                        {d[valueKey] > 999 ? `₹${Math.round(d[valueKey] / 1000)}k` : d[valueKey]}
                    </div>
                    <div className="barchart-bar" style={{
                        background: `linear-gradient(180deg, ${color}, ${color}88)`,
                        height: `${Math.max(4, (d[valueKey] / max) * 90)}px`
                    }} />
                    <div className="barchart-label">{d[labelKey]?.slice(-5)}</div>
                </div>
            ))}
        </div>
    );
}

// ── Loading Spinner ────────────────────────────────────────
export function LoadingSpinner() {
    return <div className="spinner-wrapper"><span className="spinner" /></div>;
}

// ── Role-gated page definitions ────────────────────────────
const PAGES = [
    { id: "dashboard", label: "Dashboard", icon: <MdDashboard />, path: "/admin/dashboard", minRole: "employee" },
    { id: "orders", label: "Orders", icon: <FiPackage />, path: "/admin/orders", minRole: "employee" },
    { id: "products", label: "Products", icon: <FiTag />, path: "/admin/products", minRole: "admin" },
    { id: "category", label: "Category", icon: <MdCategory />, path: "/admin/category", minRole: "admin" },
    { id: "coupons", label: "Coupons", icon: <FiGift />, path: "/admin/coupons", minRole: "admin" },
    { id: "staff", label: "Staff", icon: <FiUserPlus />, path: "/admin/staff", minRole: "admin" }, // ← NEW
    { id: "users", label: "Users", icon: <FiUsers />, path: "/admin/users", minRole: "superadmin" },
    // { id: "contacts", label: "Messages", icon: <FiMail />, path: "/admin/contacts", minRole: "employee" },
];

function canAccess(userRole, minRole) {
    return (ROLE_HIERARCHY[userRole] ?? 0) >= (ROLE_HIERARCHY[minRole] ?? 99);
}

// ── Main AdminPanel — receives user/logout as PROPS, no hook call ──
export default function AdminPanel({ user, logout, updateUser }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [toasts, setToasts] = useState([]);

    const allowedPages = useMemo(
        () => PAGES.filter(p => canAccess(user?.role, p.minRole)),
        [user?.role]
    );

    const getCurrentPage = () => {
        const page = PAGES.find(p => location.pathname === p.path);
        return page ? page.id : allowedPages[0]?.id ?? "dashboard";
    };

    const [activePage, setActivePage] = useState(getCurrentPage);

    useEffect(() => {
        setActivePage(getCurrentPage());
    }, [location.pathname]);

    const toast = useCallback((msg, type = "success") => {
        const id = Date.now();
        setToasts(p => [...p, { id, msg, type }]);
        setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
    }, []);

    const handleNavigation = (path, pageId) => {
        setActivePage(pageId);
        navigate(path);
    };

    const activeMeta = PAGES.find(p => p.id === activePage);
    const roleMeta = ROLE_META[user?.role] ?? ROLE_META.user;
    const defaultPath = allowedPages[0]?.path ?? "/login";

    return (
        <>
            <div className="admin-layout">
                {/* ── Sidebar ── */}
                <aside className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-logo"><FaCrown /></div>
                        {sidebarOpen && (
                            <div className="sidebar-brand">
                                <div className="sidebar-brand-name">KKB & Co.</div>
                                <div className="sidebar-brand-sub">Staff Console</div>
                            </div>
                        )}
                    </div>

                    {/* Role badge */}
                    {sidebarOpen && (
                        <div style={{
                            margin: "0 12px 12px",
                            padding: "8px 12px",
                            borderRadius: 8,
                            background: roleMeta.color + "18",
                            border: `1px solid ${roleMeta.color}30`,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                            color: roleMeta.color,
                            fontWeight: 600,
                        }}>
                            <span style={{ fontSize: 14 }}>{roleMeta.icon}</span>
                            {roleMeta.label}
                        </div>
                    )}

                    {sidebarOpen && <div className="sidebar-section-label">Navigation</div>}

                    <nav className="sidebar-nav">
                        {allowedPages.map(p => (
                            <button
                                key={p.id}
                                onClick={() => handleNavigation(p.path, p.id)}
                                className={`nav-item ${activePage === p.id ? 'active' : ''}`}
                                title={!sidebarOpen ? p.label : ""}
                            >
                                <span className="nav-icon">{p.icon}</span>
                                {sidebarOpen && <span className="nav-label">{p.label}</span>}
                                {sidebarOpen && activePage === p.id && <span className="nav-active-dot" />}
                            </button>
                        ))}
                    </nav>

                    <div className="sidebar-footer">
                        <button onClick={() => setSidebarOpen(o => !o)} className="sidebar-toggle">
                            {sidebarOpen ? <FiChevronLeft /> : <FiChevronRight />}
                            {sidebarOpen && <span>Collapse</span>}
                        </button>
                        {sidebarOpen && (
                            <button onClick={logout} className="sidebar-logout">
                                <FiLogOut /><span>Sign Out</span>
                            </button>
                        )}
                    </div>
                </aside>

                {/* ── Main ── */}
                <main className="main-container">
                    <header className="topbar">
                        <div className="topbar-left">
                            <button className="topbar-menu-btn" onClick={() => setSidebarOpen(o => !o)}>
                                <FiMenu />
                            </button>
                            <div className="topbar-breadcrumb">
                                <span className="topbar-breadcrumb-root">Admin</span>
                                <FiChevronRight className="topbar-breadcrumb-sep" />
                                <span className="topbar-breadcrumb-current">{activeMeta?.label}</span>
                            </div>
                        </div>
                        <div className="topbar-right">
                            <a href="/" target="_blank" rel="noopener noreferrer" className="topbar-store-link">
                                <FaStore /><span>View Store</span>
                            </a>
                            <div className="topbar-divider" />
                            <div className="user-chip">
                                <div className="user-avatar">
                                    {user?.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="user-info">
                                    <div className="user-name">{user?.name?.split(" ")[0]}</div>
                                    <div className="user-role" style={{ color: roleMeta.color }}>
                                        {roleMeta.label}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    <div className="page-heading">
                        <div className="page-heading-icon">{activeMeta?.icon}</div>
                        <div>
                            <h1 className="page-heading-title">{activeMeta?.label}</h1>
                            <p className="page-heading-sub">
                                Manage your {activeMeta?.label?.toLowerCase()} here
                            </p>
                        </div>
                    </div>

                    <div className="page-content">
                        <Routes>
                            {canAccess(user?.role, "employee") && (
                                <>
                                    <Route path="/dashboard" element={<Dashboard toast={toast} />} />
                                    <Route path="/orders" element={<Orders toast={toast} />} />
                                    <Route path="/contacts" element={<Contacts toast={toast} />} />
                                </>
                            )}
                            {canAccess(user?.role, "admin") && (
                                <>
                                    <Route path="/products" element={<Products toast={toast} />} />
                                    <Route path="/category" element={<Categories toast={toast} />} />
                                    <Route path="/coupons" element={<Coupons toast={toast} />} />
                                    <Route path="/staff" element={<Staff toast={toast} user={user} />} /> {/* ← NEW */}
                                </>
                            )}
                            {canAccess(user?.role, "superadmin") && (
                                <Route path="/users" element={<Users toast={toast} />} />
                            )}
                            <Route path="*" element={<Navigate to={defaultPath} replace />} />
                        </Routes>
                    </div>
                </main>
            </div>

            <Toast toasts={toasts} />
        </>
    );
}